import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, ChevronsUpDown, SearchX } from 'lucide-react';

import type {
  AuditLogListItem,
  LogSortDir,
  LogSortKey,
  LogSource,
  LogStatus,
} from '../types/log.types';
import { formatLogTime } from '../utils/format-log';
import { LogStatusBadge } from './LogStatusBadge';
import { LogSystemBadge } from './LogSystemBadge';

// How the table is currently ordered, and what to do when a header is clicked.
// Omitted on the skeleton and empty states, where the headers stay inert.
export interface LogSortState {
  sort: LogSortKey;
  dir: LogSortDir;
  onSort: (key: LogSortKey) => void;
}

interface LogInquiryTableProps {
  logs: readonly AuditLogListItem[];
  sortState?: LogSortState;
}

interface ColumnDef {
  // i18n key under admin ns `logInquiry.list.columns.*` for the header label.
  i18nKey: string;
  width?: number;
  // The wire sort name this column maps to. Absent = the column is not sortable
  // (only the IP-address column, per the customer document).
  sortKey?: LogSortKey;
}

// 9 columns (Time / User / Source / User Type / Category / Action / Target /
// IP / Status). Eight of them are sortable on the server; the IP-address column
// is deliberately not.
const COLUMNS: readonly ColumnDef[] = [
  { i18nKey: 'occurredAt', width: 160, sortKey: 'createdAt' },
  { i18nKey: 'user', width: 180, sortKey: 'actorName' },
  { i18nKey: 'system', width: 100, sortKey: 'source' },
  { i18nKey: 'userType', width: 130, sortKey: 'userType' },
  { i18nKey: 'category', width: 130, sortKey: 'category' },
  { i18nKey: 'action', width: 160, sortKey: 'actionCode' },
  { i18nKey: 'target', width: 140, sortKey: 'target' },
  { i18nKey: 'ipAddress', width: 130 },
  { i18nKey: 'status', width: 90, sortKey: 'status' },
];

// A sortable header: the label, plus an arrow showing how this column is
// ordered right now (up = ascending, down = descending) or a neutral
// double-arrow when the table is ordered by some other column.
function SortableHeaderLabel({
  label,
  active,
  dir,
}: {
  label: string;
  active: boolean;
  dir: LogSortDir;
}) {
  const { t } = useTranslation('admin');
  const hint = !active
    ? t('logInquiry.list.sort.sortable')
    : dir === 'asc'
      ? t('logInquiry.list.sort.ascending')
      : t('logInquiry.list.sort.descending');
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      aria-label={`${label} — ${hint}`}
    >
      {label}
      {!active ? (
        <ChevronsUpDown size={14} aria-hidden style={{ opacity: 0.4 }} />
      ) : dir === 'asc' ? (
        <ChevronUp size={14} aria-hidden />
      ) : (
        <ChevronDown size={14} aria-hidden />
      )}
    </span>
  );
}

function ColumnHeaders({ sortState }: { sortState?: LogSortState }) {
  const { t } = useTranslation('admin');
  return (
    <tr>
      {COLUMNS.map((col) => {
        const label = t(`logInquiry.list.columns.${col.i18nKey}`);
        const sortable = Boolean(col.sortKey && sortState);
        const active = sortable && sortState?.sort === col.sortKey;
        return (
          <th
            key={col.i18nKey}
            style={{
              width: col.width,
              whiteSpace: 'nowrap',
              cursor: sortable ? 'pointer' : undefined,
              userSelect: sortable ? 'none' : undefined,
            }}
            // Tells a screen reader how this column is ordered; 'none' marks a
            // sortable column that is not the active one.
            aria-sort={
              !sortable
                ? undefined
                : active
                  ? sortState?.dir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none'
            }
            onClick={
              sortable ? () => sortState?.onSort(col.sortKey!) : undefined
            }
          >
            {sortable ? (
              <SortableHeaderLabel
                label={label}
                active={Boolean(active)}
                dir={sortState?.dir ?? 'desc'}
              />
            ) : (
              label
            )}
          </th>
        );
      })}
    </tr>
  );
}

// SHCT-643 — the only action whose `params` this screen expands today. A PRO deployment's summary is
// the one audit detail a reader has to be able to reconstruct later ("what actually went out that
// day"), and it is written once and never correctable. Other actions carry `params` too, but their
// shapes differ and none has been specified for display, so they are deliberately left un-expanded
// rather than rendered as raw JSON.
const EXPANDABLE_ACTION = 'PRO_DEPLOY';

// The five keys a PRO deployment writes, in the order the deployment itself records them, each with
// the label catalog key that carries its Korean text. Driven by a list rather than read off the object
// so a key the backend adds later cannot silently appear on screen untranslated.
const PRO_DEPLOY_PARAM_KEYS: readonly { key: string; i18nKey: string }[] = [
  { key: 'itemsCount', i18nKey: 'proDeployParam.itemsCount' },
  { key: 'surveysCount', i18nKey: 'proDeployParam.surveysCount' },
  { key: 'guidelinesCount', i18nKey: 'proDeployParam.guidelinesCount' },
  { key: 'moduleConfigChanged', i18nKey: 'proDeployParam.moduleConfigChanged' },
  { key: 'diseaseFilter', i18nKey: 'proDeployParam.diseaseFilter' },
];

function LogRow({ log }: { log: AuditLogListItem }) {
  // Coded fields render Korean via the backend label catalog (`labels` ns).
  const { t: tLabels } = useTranslation('labels');
  const [expanded, setExpanded] = useState(false);

  // Resolve a wire code to its Korean label, degrading to the CODE ITSELF when the catalog has no
  // entry for it — never to the lookup path. Without this a code the catalog has not caught up with
  // renders as `auditUserType.CRC` on screen: a developer key shown to a Korean-only user, which is
  // both unreadable and a breach of the Korean-only rule. Showing `CRC` is at least the real value.
  //
  // This mirrors what the CSV export already does on the server (`AuditLogCsvExportService.label`),
  // so the file and the screen degrade the same way and cannot disagree.
  const label = (prefix: string, code: string | null): string => {
    if (!code) return '-';
    return tLabels(`${prefix}.${code}`, { defaultValue: code });
  };

  // 대상 = target label + the id when present (e.g. "Role #7").
  const targetLabel = label('auditTarget', log.targetCode);
  const target = log.targetId ? `${targetLabel} #${log.targetId}` : targetLabel;

  // SHCT-643 — a row is expandable only when it is a PRO deployment AND the backend actually sent
  // detail for it. Both halves matter: an older row written before the detail was captured has null
  // params, and offering a toggle that opens an empty panel is worse than offering none.
  const params = log.params;
  const isExpandable = log.actionCode === EXPANDABLE_ACTION && params != null;

  const mainRow = (
    <tr>
      <td style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
        {formatLogTime(log.createdAt)}
      </td>
      <td>
        <div style={{ lineHeight: 1.4 }}>
          <span style={{ fontWeight: 600 }}>{log.actorName}</span>
          <br />
          <span style={{ color: 'var(--muted-foreground)', fontSize: 12 }}>
            {log.actorEmail}
          </span>
        </div>
      </td>
      <td>
        <LogSystemBadge source={log.source as LogSource} />
      </td>
      <td>{label('auditUserType', log.actorRoleKey)}</td>
      <td>{label('auditCategory', log.category)}</td>
      <td>
        {isExpandable ? (
          // The action name itself is the toggle — the detail belongs to the action, so there is no
          // extra control to explain. A real <button> rather than a clickable cell: it reaches the
          // keyboard and announces its open/closed state to a screen reader for free.
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              color: 'inherit',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {label('auditAction', log.actionCode)}
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        ) : (
          label('auditAction', log.actionCode)
        )}
      </td>
      <td>{target}</td>
      <td style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
        {log.ipAddress || '-'}
      </td>
      <td>
        <LogStatusBadge status={log.status as LogStatus} />
      </td>
    </tr>
  );

  if (!isExpandable) {
    return mainRow;
  }

  return (
    <>
      {mainRow}
      {expanded && (
        <tr>
          {/* Spans every column so the detail reads as part of the row above it, not as a
              tenth column — the screen's column set is fixed at nine. */}
          <td colSpan={COLUMNS.length} style={{ background: 'var(--muted)', padding: '12px 16px' }}>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'max-content 1fr',
                gap: '6px 16px',
                margin: 0,
                fontSize: 13,
              }}
            >
              {PRO_DEPLOY_PARAM_KEYS.map(({ key, i18nKey }) => (
                <Fragment key={key}>
                  <dt style={{ color: 'var(--muted-foreground)' }}>{tLabels(i18nKey)}</dt>
                  <dd style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>
                    {(key === 'diseaseFilter' &&
                      (diseaseLabelOf(params) || groupLabelOf(params?.[key], tLabels))) ||
                      formatParamValue(params?.[key], tLabels)}
                  </dd>
                </Fragment>
              ))}
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}

// FEATURE_025 (PRO Distribution Management) — the Korean name of the deployed disease code ("F32 우울 에피소드"),
// which the server records beside the code. It is data, not a code, so it is shown as recorded and never looked up
// in the catalog. Empty for rows written before the server recorded it; those fall back to the code below.
function diseaseLabelOf(params: Record<string, unknown> | null): string {
  const label = params?.diseaseFilterLabel;
  return typeof label === 'string' ? label.trim() : '';
}

// SHCT-931 — a disease-group deploy written before the server recorded that name carries only the dropdown token
// `GRP:<group name>`. It reads as the dropdown row the administrator picked ("<group name> 공통"), the suffix taken
// from the catalog's COMMON label so no Korean is authored here. Empty when either part is missing, so the row falls
// back to the code below instead of printing half a label.
function groupLabelOf(
  value: unknown,
  tLabels: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (typeof value !== 'string' || !value.startsWith(GROUP_TOKEN_PREFIX)) return '';
  const groupName = value.slice(GROUP_TOKEN_PREFIX.length).trim();
  const commonKey = 'proDeployDiseaseScope.COMMON';
  const common = tLabels(commonKey, { defaultValue: commonKey });
  return groupName && common && common !== commonKey ? `${groupName} ${common}` : '';
}

const GROUP_TOKEN_PREFIX = 'GRP:';

// Render one `params` value for display. Three shapes reach here and each needs its own treatment:
// a boolean is a Korean yes/no, not "true"; a null or missing value is a dash rather than an empty
// cell (so a reader can tell "not recorded" from "still loading"); the disease-scope codes ("ALL", "COMMON") are
// catalog codes and get their Korean label like every other code on this screen. Anything else is
// stringified as-is, which for the three counts is exactly what is wanted.
function formatParamValue(
  value: unknown,
  tLabels: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') {
    return tLabels(value ? 'common.yes' : 'common.no', { defaultValue: value ? '예' : '아니오' });
  }
  if (typeof value === 'string') {
    return tLabels(`proDeployDiseaseScope.${value}`, { defaultValue: value });
  }
  return String(value);
}

export function LogInquiryTable({ logs, sortState }: LogInquiryTableProps) {
  return (
    <div className="card">
      <table className="table" style={{ margin: 0 }}>
        <thead>
          <ColumnHeaders sortState={sortState} />
        </thead>
        <tbody>
          {logs.map((log) => (
            <LogRow key={log.id} log={log} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Skeleton = 5 rows × 9 cols (matches prototype bootstrap).
export function LogInquirySkeleton() {
  return (
    <div className="card">
      <table className="table" style={{ margin: 0 }}>
        <thead>
          <ColumnHeaders />
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i}>
              {COLUMNS.map((col) => (
                <td key={col.i18nKey}>
                  <div
                    className="animate-pulse"
                    style={{ height: 14, background: 'var(--muted)', borderRadius: 4 }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// "No logs found. Please check your search criteria again." — search-x icon +
// the single message regardless of whether any filter is active.
export function LogInquiryEmpty({ sortState }: { sortState?: LogSortState }) {
  const { t } = useTranslation('admin');
  return (
    <div className="card">
      <table className="table" style={{ margin: 0 }}>
        <thead>
          <ColumnHeaders sortState={sortState} />
        </thead>
        <tbody>
          <tr>
            <td
              colSpan={COLUMNS.length}
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: 'var(--muted-foreground)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <SearchX size={48} color="var(--muted-foreground)" />
                <p style={{ margin: 0 }}>{t('logInquiry.list.empty')}</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
